package at.theatervogelweide.admin.web.screens.sponsors

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.extensions
import at.theatervogelweide.admin.contract.formatMessage
import at.theatervogelweide.admin.contract.moved
import at.theatervogelweide.admin.contract.sponsors.SponsorDraft
import at.theatervogelweide.admin.contract.sponsors.SponsorEntry
import at.theatervogelweide.admin.contract.sponsors.SponsorService
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminCardFooterSpacer
import at.theatervogelweide.admin.web.components.AdminCheckbox
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminSelectableList
import at.theatervogelweide.admin.web.components.AdminSingleImageField
import at.theatervogelweide.admin.web.components.AdminSplitView
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.EditableImages
import at.theatervogelweide.admin.web.components.LocalPanes
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.ReorderAnnouncement
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.SelectableRow
import at.theatervogelweide.admin.web.components.SplitController
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.editorShown
import at.theatervogelweide.admin.web.components.rememberReorder
import at.theatervogelweide.admin.web.components.singleImageUuid
import at.theatervogelweide.admin.web.rpcCall
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private val button = Bem("admin-button")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val fieldset = Bem("admin-fieldset")

private const val LOGO_HINT = "Am besten als SVG oder PNG mit transparentem Hintergrund, andere Bilder mindestens 96 px hoch. " +
    "Ohne Logo steht der Name. Steht auf den Seiten „Sponsoren“ und „Kartenreservierung“."

/** The editor of one sponsor, or of a new one while [loaded] is `null`. */
private class SponsorEditorState(loaded: SponsorEntry?, val logo: EditableImages) {
    var loaded: SponsorEntry? by mutableStateOf(loaded)
    val original: SponsorDraft = loaded?.draft ?: SponsorDraft()

    /** The form without the logo, which [logo] holds. */
    var form: SponsorDraft by mutableStateOf(original)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)

    val busy: Boolean get() = saving || reloading

    val isNew: Boolean get() = loaded == null

    val draft: SponsorDraft get() = form.copy(logoUuid = logo.singleImageUuid)

    val hasChanges: Boolean get() = draft != original || logo.uploading

    fun errorFor(name: String): String? = fieldErrors.firstOrNull { it.field == name }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict || failure.fieldErrors.isNotEmpty() -> null
            failure.code == ErrorCode.NOT_FOUND -> "Diesen Sponsor gibt es nicht mehr. Bitte wähle einen anderen."
            else -> failure.message
        }
    }
}

private fun newLogo(scope: CoroutineScope, api: AdminApi, uuid: String?) = EditableImages(
    scope,
    { file -> api.uploadImage(ImageCategory.SPONSORS, file) },
    listOfNotNull(uuid),
    extensions = ImageCategory.SPONSORS.extensions,
    formatMessage = ImageCategory.SPONSORS::formatMessage,
)

/**
 * A moved order to send; [number] counts the moves, so moving back to an order sent before is a request of its own
 * and isn't merged with that one.
 */
private data class ReorderRequest(val number: Int, val order: List<Long>)

/** Runs what a screen still sends after it was left, such as its last reorder; the RPC connection outlives screens. */
private val leftScreens = MainScope()

/**
 * Sponsoren (#94): the sponsors in the public site's order beside the editor of the selected one, like Vorstand.
 * [selectedId] comes from the address, and [split] changes it; with panes and no selection the first sponsor opens.
 * [startNew] opens the form for a new sponsor (`/sponsoren/neu`).
 */
@OptIn(FlowPreview::class)
@Composable
fun SponsorsScreen(
    rpc: RpcClient,
    api: AdminApi,
    guard: UnsavedChangesGuard,
    split: SplitController,
    selectedId: Long?,
    startNew: Boolean,
) {
    val service = remember(rpc) { rpc.withService<SponsorService>() }
    val scope = rememberCoroutineScope()

    var entries by remember { mutableStateOf<List<SponsorEntry>?>(null) }
    // the order shown, which runs ahead of the server's while a reorder is on its way
    var order by remember { mutableStateOf(emptyList<Long>()) }
    var listError by remember { mutableStateOf<String?>(null) }
    var editor by remember { mutableStateOf<SponsorEditorState?>(null) }
    var editorError by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(startNew) }
    var confirmDelete by remember { mutableStateOf(false) }
    val reorderRequests = remember { MutableStateFlow<ReorderRequest?>(null) }
    // a moved order the server hasn't confirmed yet; list reloads keep it instead of the order they read
    var reorderPending by remember { mutableStateOf(false) }
    // a moved order still waiting out the debounce; leaving the screen sends it instead of dropping it
    val unsentReorder = remember { mutableStateOf(false) }
    val reorderLock = remember { Mutex() }
    // moves up to this number were dropped with "Verwerfen und wechseln" and are no longer sent
    val discardedMoves = remember { mutableStateOf(0) }

    fun editorFor(entry: SponsorEntry?) = SponsorEditorState(entry, newLogo(scope, api, entry?.logoUuid))

    suspend fun reloadList() {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                val ids = result.value.map { it.id }
                order = if (reorderPending) order.filter { it in ids } + ids.filterNot { it in order } else ids
                listError = null
            }

            is AdminResult.Failure -> listError = result.error.message
        }
    }

    LaunchedEffect(service) { reloadList() }

    // the form for a new sponsor follows the address: /sponsoren/neu opens it, leaving it for /sponsoren closes it
    LaunchedEffect(startNew) {
        when {
            startNew && editor?.isNew != true -> {
                creating = true
                editorError = null
                editor = editorFor(null)
            }

            !startNew && creating && selectedId == null -> {
                editor = null
                creating = false
            }
        }
    }

    // back on the list alone, the editor and its changes are gone
    val editorVisible = editorShown(selectedId, startNew)
    LaunchedEffect(editorVisible) {
        if (!editorVisible) {
            editor = null
            editorError = null
            creating = false
        }
    }

    // the list holds every sponsor with all the editor needs, so selecting needs no request; with panes and without
    // a selection, the first sponsor opens
    val panes = LocalPanes.current
    LaunchedEffect(entries, selectedId, creating, panes) {
        val all = entries ?: return@LaunchedEffect
        when {
            creating -> Unit
            selectedId == null -> if (panes) all.firstOrNull()?.let { split.show(it.id) }
            editor?.loaded?.id != selectedId -> {
                val entry = all.firstOrNull { it.id == selectedId }
                editor = entry?.let { editorFor(it) }
                editorError = if (entry == null) "Diesen Sponsor gibt es nicht mehr." else null
            }
        }
    }

    /** Sends [request] with the versions of the latest list and takes in the answer. */
    suspend fun sendReorder(request: ReorderRequest) {
        if (request.number <= discardedMoves.value) {
            return
        }
        val requested = request.order
        val versions = entries.orEmpty().associate { it.id to it.version }
        // a sponsor added or removed since the move: the server needs exactly the current ones, new ones at the end
        val sponsors = requested.filter { it in versions } + versions.keys.filterNot { it in requested }
        val result = rpcCall { service.reorder(sponsors.map { VersionedId(it, versions.getValue(it)) }) }
        // a later move is waiting: the shown order stays ahead of the server's until that one is answered too
        val latest = reorderRequests.value?.number ?: 0
        val laterMove = latest != request.number && latest > discardedMoves.value
        when (result) {
            is AdminResult.Success -> {
                entries = result.value
                if (!laterMove) {
                    order = result.value.map { it.id }
                }
                reorderPending = laterMove
                listError = null
                // the open sponsor moved too; its new version is the one to save against
                editor?.let { state ->
                    val loaded = state.loaded ?: return@let
                    val after = result.value.firstOrNull { it.id == loaded.id }?.version ?: return@let
                    if (versions[loaded.id] == loaded.version) {
                        state.loaded = loaded.copy(version = after)
                    }
                }
            }

            is AdminResult.Failure -> {
                val message = if (result.error.code == ErrorCode.CONFLICT) {
                    "Die Liste hat sich inzwischen geändert. Sie ist jetzt neu geladen; bitte verschiebe noch einmal."
                } else {
                    "Die Reihenfolge konnte nicht gespeichert werden. ${result.error.message}"
                }
                reorderPending = laterMove
                reloadList()
                // the reload succeeding doesn't make the move's failure go away
                listError = message
            }
        }
    }

    /**
     * Runs a change of a sponsor behind the reorders: a moved order still waiting is sent first, so the change starts
     * from the versions it answered with and neither makes the other a conflict.
     */
    suspend fun <T> afterReorders(change: suspend () -> T): T = reorderLock.withLock {
        val waiting = reorderRequests.value
        if (unsentReorder.value && waiting != null) {
            unsentReorder.value = false
            sendReorder(waiting)
        }
        change()
    }

    // reorders are sent one after another, each with the versions of the latest list; leaving the screen neither takes
    // back one on its way nor lets the last one overtake it
    LaunchedEffect(service) {
        reorderRequests.filterNotNull().debounce(400).collect { requested ->
            unsentReorder.value = false
            withContext(NonCancellable) { reorderLock.withLock { sendReorder(requested) } }
        }
    }

    DisposableEffect(service) {
        onDispose {
            val requested = reorderRequests.value
            if (unsentReorder.value && requested != null) {
                leftScreens.launch { reorderLock.withLock { sendReorder(requested) } }
            }
        }
    }

    val state = editor.takeIf { editorVisible }
    val hasChanges = state?.hasChanges == true
    // a move not sent yet counts too, so signing out or closing the tab asks first; one already on its way is being
    // saved, not unsaved, and lands whatever the person answers
    ReportUnsavedChanges(guard, hasChanges || unsentReorder.value) {
        // "Verwerfen und wechseln": the moves not sent yet are dropped and the list shows the server's order again,
        // until the answer to a move on its way brings in what it stored
        discardedMoves.value = reorderRequests.value?.number ?: 0
        if (unsentReorder.value) {
            unsentReorder.value = false
            reorderPending = false
            order = entries.orEmpty().map { it.id }
        }
    }

    fun select(id: Long) {
        if (id == selectedId && !creating) {
            return
        }
        guard.proceed {
            creating = false
            if (editor?.loaded?.id != id) {
                editor = null
            }
            split.open(id)
        }
    }

    fun startNew() {
        guard.proceed {
            creating = true
            editorError = null
            editor = editorFor(null)
            split.openNew()
        }
    }

    fun save() {
        val current = editor ?: return
        val draft = current.draft.let { it.copy(name = it.name.trim(), websiteUrl = it.websiteUrl.trim()) }
        val errors = draft.validate()
        current.fieldErrors = errors
        current.error = null
        if (errors.isNotEmpty() || current.saving || current.logo.uploading) {
            return
        }

        current.saving = true
        scope.launch {
            val result = afterReorders {
                // read only now: a reorder sent just before may have given the sponsor a new version
                val loaded = current.loaded
                rpcCall { if (loaded == null) service.create(draft) else service.save(loaded.versionedId, draft) }
                    // the next reorder needs the new versions
                    .also { if (it is AdminResult.Success) reloadList() }
            }
            current.saving = false
            // the person may have switched to another sponsor meanwhile; the list shows the result already
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = editorFor(result.value)
                    creating = false
                    split.show(result.value.id)
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun discard() {
        val current = editor ?: return
        val loaded = current.loaded
        if (loaded == null) {
            editor = null
            creating = false
            split.backToList()
        } else {
            editor = editorFor(loaded)
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                reloadList()
            } finally {
                current.reloading = false
            }
            if (editor === current) {
                val id = current.loaded?.id
                val entry = entries?.firstOrNull { it.id == id }
                editor = entry?.let { editorFor(it) }
                editorError = if (entry == null) "Diesen Sponsor gibt es nicht mehr." else null
            }
        }
    }

    fun delete() {
        confirmDelete = false
        val current = editor ?: return
        val loaded = current.loaded ?: return
        scope.launch {
            val result = afterReorders {
                rpcCall { service.delete((current.loaded ?: loaded).versionedId) }.also { if (it is AdminResult.Success) reloadList() }
            }
            // the person may have switched to another sponsor meanwhile; the list shows the result already
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = null
                    entries = entries?.filterNot { it.id == loaded.id }
                    order = order - loaded.id
                    split.backToList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    AdminPageHeader("Sponsoren", unsavedChanges = hasChanges) {
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::startNew) { Text("+ Sponsor") }
    }

    val byId = entries.orEmpty().associateBy { it.id }
    val ordered = order.mapNotNull { byId[it] }
    val reorder = rememberReorder(
        keys = ordered.map { it.id },
        describe = { id -> byId[id]?.name ?: "Sponsor" },
    ) { from, to ->
        order = order.moved(from, to)
        reorderPending = true
        unsentReorder.value = true
        reorderRequests.value = ReorderRequest((reorderRequests.value?.number ?: 0) + 1, order)
    }

    AdminSplitView(
        controller = split,
        selectedId = selectedId,
        new = startNew,
        name = "Sponsoren",
        listLabel = "Liste der Sponsoren",
        list = {
            listError?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            when {
                entries == null -> P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                ordered.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Noch keine Sponsoren. Füge mit „+ Sponsor“ den ersten hinzu.")
                }

                else -> AdminSelectableList(
                    rows = ordered.map { sponsor ->
                        SelectableRow(
                            key = sponsor.id,
                            title = sponsor.name,
                            meta = sponsor.websiteHost ?: "ohne Website",
                            thumbnailUrl = sponsor.logoUuid?.let { api.imageUrl(ImageCategory.SPONSORS, it) },
                            badge = if (sponsor.visible) null else { { AdminBadge("Ausgeblendet", BadgeVariant.HIDDEN) } },
                            logo = true,
                        )
                    },
                    selected = if (creating) null else selectedId,
                    onSelect = ::select,
                    recent = split.recent,
                    reorder = reorder.takeIf { ordered.size > 1 },
                )
            }
            ReorderAnnouncement(reorder)
        },
        detail = {
            when {
                state != null -> key(state) {
                    DisposableEffect(state) { onDispose { state.logo.dispose() } }
                    SponsorEditorCard(
                        state = state,
                        api = api,
                        onSave = ::save,
                        onDiscard = ::discard,
                        onDelete = { confirmDelete = true },
                        onReload = ::reloadAfterConflict,
                    )
                }

                editorError != null -> P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(editorError!!) }
            }
        },
    )

    AdminConfirmDialog(
        open = confirmDelete,
        id = "sponsor-loeschen",
        title = "Sponsor löschen?",
        message = "„${state?.loaded?.name.orEmpty()}“ verschwindet mit seinem Logo von den Seiten „Sponsoren“ und " +
            "„Kartenreservierung“ der Website. Das lässt sich nicht rückgängig machen.",
        confirmLabel = "Löschen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = false },
        danger = true,
    )
}

@Composable
private fun SponsorEditorCard(
    state: SponsorEditorState,
    api: AdminApi,
    onSave: () -> Unit,
    onDiscard: () -> Unit,
    onDelete: () -> Unit,
    onReload: () -> Unit,
) {
    val form = state.form
    val canSave = !state.busy && !state.logo.uploading && (state.hasChanges || state.isNew)

    Form(attrs = {
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onSave()
        }
    }) {
        AdminCard(
            title = if (state.isNew) "Neuer Sponsor" else "Sponsor bearbeiten",
            id = "sponsor",
            header = state.loaded?.takeIf { !it.visible }?.let { { AdminBadge("Ausgeblendet", BadgeVariant.HIDDEN) } },
            footer = if (state.isNew) {
                null
            } else {
                {
                    AdminButton(variant = ButtonVariant.DANGER, disabled = state.busy, onClick = onDelete) { Text("Löschen") }
                    AdminCardFooterSpacer()
                    PublicSiteLink("/sponsors", button.block, button.modifier("link")) { Text("Auf Website ansehen ↗") }
                }
            },
            saveActions = {
                AdminButton(disabled = state.busy || (!state.hasChanges && !state.isNew), onClick = onDiscard) { Text("Verwerfen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    type = ButtonType.Submit,
                    disabled = !canSave,
                ) { Text(if (state.saving) "Speichert …" else "Speichern") }
            },
        ) {
            if (state.conflict) {
                AdminConflictCallout(onReload = onReload)
            }
            state.error?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            Fieldset({
                classes(fieldset.block)
                if (state.busy) {
                    attr("disabled", "")
                }
            }) {
                AdminTextField(
                    id = "sponsor-${SponsorDraft.NAME}",
                    label = "Name",
                    value = form.name,
                    onValueChange = { state.form = state.form.copy(name = it) },
                    error = state.errorFor(SponsorDraft.NAME),
                    hint = "Steht als Text für das Logo, z. B. für Screenreader.",
                    autocomplete = "off",
                )
                AdminTextField(
                    id = "sponsor-${SponsorDraft.WEBSITE}",
                    label = "Website",
                    value = form.websiteUrl,
                    onValueChange = { state.form = state.form.copy(websiteUrl = it) },
                    type = InputType.Url,
                    error = state.errorFor(SponsorDraft.WEBSITE),
                    hint = "Ein Klick auf das Logo öffnet diese Seite.",
                    optional = true,
                    autocomplete = "off",
                    placeholder = "https://…",
                    inputMode = "url",
                )
                AdminSingleImageField(
                    id = "sponsor-${SponsorDraft.LOGO}",
                    label = "Logo",
                    images = state.logo,
                    imageUrl = { api.imageUrl(ImageCategory.SPONSORS, it) },
                    hint = LOGO_HINT,
                    error = state.errorFor(SponsorDraft.LOGO),
                    disabled = state.busy,
                    logo = true,
                    optional = true,
                )
                AdminCheckbox(
                    label = "Auf der Website zeigen",
                    checked = form.visible,
                    onCheckedChange = { state.form = state.form.copy(visible = it) },
                )
            }
        }
    }
}
